import React, { useState } from "react";
import { OddsButton } from "./sb";
import { resolvePlayerHeadshot } from "@/lib/sportsAssets";

/* PlayerPropRow (design SBXPropRow.dc.html) — a two-way prop row: headshot + name/label,
   a LINE cell, and Over/Under OddsButton stacks. Shared by the event Popular preview and
   the Player Props tab. Headshot falls back to initials. */

const initialsOf = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
};

// Surname for compact labels — drops trailing suffix tokens (Jr., III, …).
const SUFFIX = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"]);
export const surname = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  while (w.length > 1 && SUFFIX.has(w[w.length - 1].toLowerCase())) w.pop();
  return w[w.length - 1] || "";
};

export default function PlayerPropRow({ player, propLabel, line, over, under, yesno = false, onPick, picked, mobile = false, last = false, hideName = false }) {
  const src = resolvePlayerHeadshot({ player, providerImageUrl: over?.player_image_url || under?.player_image_url || null });
  const [broken, setBroken] = useState(false);
  const showImg = src && !broken;
  const hs = mobile ? 38 : 44;
  const grid = mobile ? "minmax(0,1fr) 44px 74px 74px" : "minmax(0,1fr) 64px 96px 96px";
  const title = hideName ? propLabel : player;
  const sub = hideName ? `${surname(player)}${yesno ? "" : " · O/U"}` : propLabel;
  const stack = (o, label) => (o
    ? <OddsButton variant="stack" line={label} price={o.odds} selected={picked(o)} locked={o.suspended === true || o.active === false} onClick={() => onPick(o)} testid={`odds-${o.id}`} />
    : <OddsButton na variant="stack" />);
  return (
    <div style={{ display: "grid", gridTemplateColumns: grid, alignItems: "center", columnGap: mobile ? 6 : 10, padding: mobile ? "10px 12px" : "12px 18px", borderBottom: `1px solid ${last ? "transparent" : "#1A1A1E"}` }} data-testid={`prop-row-${over?.id || under?.id || player}`}>
      <div className="flex min-w-0 items-center" style={{ gap: mobile ? 10 : 12 }}>
        <span className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-full" style={{ width: hs, height: hs, background: "radial-gradient(circle at 50% 30%, #26262B 0%, #18181C 70%)", boxShadow: "inset 0 0 0 1px #2F2F35, inset 0 1px 0 rgba(255,255,255,.08)" }}>
          {showImg
            ? <img src={src} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" onError={() => setBroken(true)} />
            : <span style={{ font: `800 ${mobile ? 12 : 14}px Manrope`, letterSpacing: "-.02em", color: "#D4D4D8" }}>{initialsOf(player)}</span>}
        </span>
        <div className="flex min-w-0 flex-col" style={{ gap: 3 }}>
          <span className="truncate" style={{ font: `700 ${mobile ? 14 : 15}px Outfit`, color: "#fff" }}>{title}</span>
          <span className="truncate" style={{ font: "500 12px Outfit", color: "#8A8A93" }}>{sub}</span>
        </div>
      </div>
      <div className="flex flex-col items-center" style={{ gap: 2 }}>
        {!yesno && <>
          <span style={{ font: "700 9px Outfit", letterSpacing: ".16em", color: "#52525b" }}>LINE</span>
          <span className="tabular-nums" style={{ font: `800 ${mobile ? 15 : 18}px/1 Manrope`, color: "#fff" }}>{line ?? "—"}</span>
        </>}
      </div>
      {stack(over, yesno ? "Yes" : "Over")}
      {stack(under, yesno ? "No" : "Under")}
    </div>
  );
}

// Group a player's raw prop outcomes into rows keyed by (prop_short, line). Over/Under props fill the
// over/under slots; Yes/No props (anytime TD / goal scorer — no line) reuse the same slots (Yes->over,
// No->under) with a `yesno` flag so the row renders Yes/No buttons and hides the line cell.
export function groupPlayerProps(outcomes = []) {
  const byKey = {};
  outcomes.forEach((o) => {
    const isYesNo = o.side === "yes" || o.side === "no";
    const key = `${o.prop_short || ""}|${o.line ?? ""}`;
    const row = (byKey[key] = byKey[key] || { prop: o.prop_short || "", line: o.line, over: null, under: null, yesno: false });
    if (o.side === "under" || o.side === "no") row.under = o; else row.over = o;
    if (isYesNo) row.yesno = true;
  });
  return Object.values(byKey);
}
